import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users,
  ChatCircleDots,
  HandsPraying,
  BookOpen,
  Globe,
  Warning,
  ShieldCheck,
  BookBookmark,
  HandCoins,
  SpinnerGap,
  Eye,
  Check,
  Clock,
  Sun,
  Moon,
} from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import AdminContentPanel from "@/components/admin/AdminContentPanel";
import FunnelPanel from "@/components/admin/FunnelPanel";
import EarlySupportersPanel from "@/components/admin/EarlySupportersPanel";
import CirclesAdminPanel from "@/components/admin/CirclesAdminPanel";
import UsersAdminPanel from "@/components/admin/UsersAdminPanel";

const PIE_COLORS = ["#D4AF37", "#3B82F6", "#F43F5E", "#10B981", "#8B5CF6", "#6B7280"];
const severityColors = { high: "bg-red-500/20 text-red-400", medium: "bg-amber-500/20 text-amber-400", low: "bg-blue-500/20 text-blue-400" };
const statusColors = { pending: "bg-slate-500/20 text-slate-400", review: "bg-amber-500/20 text-amber-400", approved: "bg-emerald-500/20 text-emerald-400" };

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [theme, setTheme] = useState(() => localStorage.getItem("vg-admin-theme") || "light");

  const dark = theme === "dark";
  const toggleTheme = () => {
    const next = dark ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("vg-admin-theme", next);
  };

  // Theme-aware class helpers
  const bg = dark ? "bg-[#020617]" : "bg-slate-50";
  const cardCls = dark ? "bg-[#0F172A] border-white/5" : "bg-white border-slate-200 shadow-sm";
  const innerCls = dark ? "bg-[#1E293B] border-white/5" : "bg-slate-100 border-slate-200";
  const txt = dark ? "text-white" : "text-slate-900";
  const txtSec = dark ? "text-slate-400" : "text-slate-600";
  const txtMut = dark ? "text-slate-500" : "text-slate-500";
  const tabBg = dark ? "bg-[#1E293B]" : "bg-slate-100";
  const tabActive = dark
    ? "data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617]"
    : "data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617]";
  const tooltipBg = dark
    ? { background: "#1E293B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: "#fff", fontFamily: "Outfit" }
    : { background: "#fff", border: "1px solid #E2E8F0", borderRadius: "8px", color: "#020617", fontFamily: "Outfit" };
  const axisTick = dark ? { fill: "#94A3B8", fontSize: 12, fontFamily: "Outfit" } : { fill: "#64748B", fontSize: 12, fontFamily: "Outfit" };
  const langPillBg = dark ? "bg-[#1E293B] border-white/5" : "bg-slate-100 border-slate-200";
  const langTxt = dark ? "text-white" : "text-slate-800";

  useEffect(() => {
    api.get("/admin/stats")
      .then((res) => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${bg}`}>
        <SpinnerGap size={32} className="text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className={`p-6 sm:p-8 max-w-6xl mx-auto min-h-screen ${bg} transition-colors duration-300`} data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className={`font-heading text-2xl sm:text-3xl font-medium ${txt}`}>
            Admin Dashboard
          </h1>
          <p className={`font-body text-sm mt-1 ${txtSec}`}>
            Voice Global oversight and analytics
          </p>
        </div>
        <Button
          onClick={toggleTheme}
          variant="outline"
          data-testid="theme-toggle-btn"
          className={`rounded-full w-10 h-10 p-0 ${dark ? "border-white/20 text-white hover:bg-white/10" : "border-slate-300 text-slate-700 hover:bg-slate-100"}`}
        >
          {dark ? <Sun size={18} /> : <Moon size={18} />}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {[
          { icon: Users, label: "Total Users", value: stats.total_users?.toLocaleString() },
          { icon: Users, label: "Active Today", value: stats.active_today },
          { icon: ChatCircleDots, label: "Conversations", value: stats.conversations_today?.toLocaleString() },
          { icon: HandsPraying, label: "Prayers", value: stats.prayers_generated?.toLocaleString() },
          { icon: BookOpen, label: "Verses Shared", value: stats.verses_shared?.toLocaleString() },
          { icon: HandCoins, label: "Donations", value: `$${(stats.total_donations_usd / 1000).toFixed(1)}K` },
        ].map((stat, i) => (
          <Card key={i} className={cardCls} data-testid={`admin-stat-${i}`}>
            <CardContent className="p-4">
              <stat.icon size={18} weight="duotone" className="text-[#D4AF37] mb-2" />
              <p className={`font-heading text-xl font-medium ${txt}`}>{stat.value}</p>
              <p className={`text-xs font-body ${txtMut}`}>{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className={`${tabBg} mb-6`}>
          {["overview", "users", "content", "supporters", "circles", "flagged", "theology", "sources"].map((t) => (
            <TabsTrigger key={t} value={t} className={`font-body text-xs capitalize ${tabActive}`} data-testid={`admin-tab-${t}`}>
              {t === "overview" ? "Analytics" : t === "users" ? "Users" : t === "content" ? "Content" : t === "supporters" ? "Supporters" : t === "circles" ? "Circles" : t === "flagged" ? "Flagged Content" : t === "theology" ? "Theology Review" : "Knowledge Sources"}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview">
          <div className="mb-6">
            <FunnelPanel />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Conversations Chart */}
            <Card className={cardCls} data-testid="conversations-chart">
              <CardContent className="p-6">
                <h3 className={`font-heading text-lg mb-4 font-medium ${txt}`}>Daily Conversations</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={stats.daily_conversations}>
                    <XAxis dataKey="day" tick={axisTick} axisLine={false} tickLine={false} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={tooltipBg} />
                    <Bar dataKey="count" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Bot Usage */}
            <Card className={cardCls} data-testid="bot-usage-chart">
              <CardContent className="p-6">
                <h3 className={`font-heading text-lg mb-4 font-medium ${txt}`}>Bot Usage Distribution</h3>
                <div className="flex items-center gap-6">
                  <ResponsiveContainer width={160} height={160}>
                    <PieChart>
                      <Pie
                        data={Object.entries(stats.bot_usage).map(([name, value]) => ({ name, value }))}
                        cx="50%" cy="50%" innerRadius={45} outerRadius={70}
                        dataKey="value" stroke="none"
                      >
                        {Object.keys(stats.bot_usage).map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2">
                    {Object.entries(stats.bot_usage).map(([name, value], i) => (
                      <div key={name} className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i] }} />
                        <span className={`text-xs font-body capitalize ${txtSec}`}>{name}</span>
                        <span className={`text-xs font-body ml-auto ${txtMut}`}>{value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Language Distribution */}
            <Card className={`lg:col-span-2 ${cardCls}`} data-testid="language-chart">
              <CardContent className="p-6">
                <h3 className={`font-heading text-lg mb-4 font-medium ${txt}`}>Language Distribution</h3>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(stats.languages).map(([name, value], i) => (
                    <div key={i} className={`flex items-center gap-2 rounded-xl px-4 py-2 border ${langPillBg}`}>
                      <Globe size={14} weight="duotone" className="text-[#D4AF37]" />
                      <span className={`text-sm font-body ${langTxt}`}>{name}</span>
                      <span className={`text-xs font-body ${txtMut}`}>{value}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="content">
          <AdminContentPanel />
        </TabsContent>

        <TabsContent value="users">
          <UsersAdminPanel />
        </TabsContent>

        <TabsContent value="supporters">
          <EarlySupportersPanel adminEmail={(JSON.parse(localStorage.getItem("vg_user") || "{}")?.email) || ""} />
        </TabsContent>

        <TabsContent value="circles">
          <CirclesAdminPanel />
        </TabsContent>

        <TabsContent value="flagged">
          <Card className={cardCls} data-testid="flagged-content-table">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Warning size={20} weight="duotone" className="text-amber-400" />
                <h3 className={`font-heading text-lg font-medium ${txt}`}>Flagged Content Queue</h3>
              </div>
              <div className="space-y-3">
                {stats.flagged_content?.map((item) => (
                  <div key={item.id} className={`rounded-xl p-4 border ${innerCls}`} data-testid={`flagged-${item.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className={severityColors[item.severity] + " text-xs font-body"}>{item.severity}</Badge>
                        <span className={`text-xs font-body ${txtSec}`}>{item.bot}</span>
                      </div>
                      <Badge className={statusColors[item.status] + " text-xs font-body"}>{item.status}</Badge>
                    </div>
                    <p className={`text-sm font-body ${txt}`}>{item.reason}</p>
                    <p className={`text-xs font-body mt-1 ${txtMut}`}>{item.user_email} - {new Date(item.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="theology">
          <Card className={cardCls} data-testid="theology-review-table">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck size={20} weight="duotone" className="text-[#D4AF37]" />
                <h3 className={`font-heading text-lg font-medium ${txt}`}>Theology Review Queue</h3>
              </div>
              <div className="space-y-3">
                {stats.theology_review?.map((item) => (
                  <div key={item.id} className={`rounded-xl p-4 border ${innerCls}`} data-testid={`theology-${item.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-body capitalize ${txtSec}`}>{item.bot}</span>
                      <Badge className={statusColors[item.status] + " text-xs font-body"}>
                        {item.status === "pending" && <Clock size={10} className="mr-1" />}
                        {item.status === "approved" && <Check size={10} className="mr-1" />}
                        {item.status === "review" && <Eye size={10} className="mr-1" />}
                        {item.status}
                      </Badge>
                    </div>
                    <p className={`text-sm font-body ${txt}`}>{item.topic}</p>
                    <p className={`text-xs font-body mt-1 ${txtMut}`}>{new Date(item.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sources">
          <Card className={cardCls} data-testid="knowledge-sources-table">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <BookBookmark size={20} weight="duotone" className="text-[#D4AF37]" />
                <h3 className={`font-heading text-lg font-medium ${txt}`}>Knowledge Sources</h3>
              </div>
              <p className={`text-sm font-body mb-4 ${txtSec}`}>
                Approved scripture and commentary sources used for AI responses.
              </p>
              <div className="space-y-3">
                {stats.knowledge_sources?.map((source) => (
                  <div key={source.id} className={`rounded-xl p-4 border flex items-center gap-4 ${innerCls}`} data-testid={`source-${source.id}`}>
                    <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] font-heading font-bold text-sm">
                      {source.priority}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-body font-medium ${txt}`}>{source.name}</p>
                      <p className={`text-xs font-body ${txtMut}`}>{source.type}</p>
                    </div>
                    <Badge className={`text-xs font-body ${source.status === "active" ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}`}>
                      {source.status === "active" ? "Active" : "Pending Review"}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
